import React, { useCallback, useEffect, useState } from "react";
import {
  blockFeedUser,
  fetchFeedUser,
  followFeedUser,
  unblockFeedUser,
  unfollowFeedUser,
} from "../../api";
import { arabicDigits } from "../../utils/arabicNumbers";
import PostCard, { avatarTint } from "./PostCard";

/**
 * FeedProfile — a user's public feed profile in majlis style: identity card
 * with follow + block controls, then their published posts. Rendered inside
 * MajlisPage (view === "profile"). The API 404s cross-gender profiles, so
 * only same-gender users can ever appear here.
 *
 * Blocking severs the pair both ways (their content disappears from your
 * feed and yours from theirs) and removes follows between you.
 */
export default function FeedProfile({
  userId,
  onBack,
  showHarakat,
  onVote,
  onOpenComments,
  renderThread,
  commentsOpenFor,
  isAdmin,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchFeedUser(userId)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [userId]);

  useEffect(load, [load]);

  const toggleFollow = async () => {
    if (!data || busy) return;
    setBusy(true);
    try {
      const res = data.user.is_following
        ? await unfollowFeedUser(userId)
        : await followFeedUser(userId);
      setData((d) => ({
        ...d,
        user: { ...d.user, is_following: res.following, follower_count: res.follower_count },
      }));
    } catch {
      /* best-effort */
    } finally {
      setBusy(false);
    }
  };

  const toggleBlock = async () => {
    if (!data || busy) return;
    if (!data.user.blocked_by_me && !window.confirm("حظر هذا العضو؟ لن يرى أحدُكما منشورات الآخر.")) {
      return;
    }
    setBusy(true);
    try {
      if (data.user.blocked_by_me) await unblockFeedUser(userId);
      else await blockFeedUser(userId);
      load(); // block changes visibility — refetch the whole profile
    } catch {
      /* best-effort */
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <p className="font-plex text-sm text-center py-8" style={{ color: "var(--mj-flag)" }}>
        {error}
      </p>
    );
  }
  if (!data) {
    return (
      <p className="font-plex text-sm text-center py-8" style={{ color: "var(--mj-muted)" }}>
        جارٍ التحميل…
      </p>
    );
  }

  const { user, results } = data;

  return (
    <div className="font-plex">
      <button
        onClick={onBack}
        className="rounded-full px-3 py-1 text-[12.5px] font-semibold mb-3"
        style={{ color: "var(--mj-muted)", border: "1px solid var(--mj-line)" }}
      >
        → عودة إلى المجلس
      </button>

      {/* identity card */}
      <div
        className="rounded-[14px] px-4 py-4 flex items-center gap-3 flex-wrap"
        style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
      >
        <span
          className="w-12 h-12 rounded-full grid place-items-center font-arabic text-xl shrink-0"
          style={{ ...avatarTint(user.id), border: "1px solid var(--mj-line)" }}
          aria-hidden="true"
        >
          {user.initial || "؟"}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold truncate" style={{ color: "var(--mj-cream)" }}>
            {user.display_name}
          </p>
          <p className="text-[12px]" style={{ color: "var(--mj-muted)" }}>
            {user.handle ? `@${user.handle} · ` : ""}
            {arabicDigits(user.follower_count)} متابِع · {arabicDigits(user.post_count)} منشور
          </p>
        </div>
        {!user.is_me && (
          <div className="flex items-center gap-2">
            {!user.is_blocked && (
              <button
                onClick={toggleFollow}
                disabled={busy}
                className="rounded-full px-4 py-1.5 text-[13px] font-bold disabled:opacity-50"
                style={
                  user.is_following
                    ? {
                        background: "var(--mj-gold-soft)",
                        color: "var(--mj-gold)",
                        border: "1px solid var(--mj-gold-line)",
                      }
                    : { background: "var(--mj-gold)", color: "var(--mj-bg)" }
                }
              >
                {user.is_following ? "متابَع ✓" : "تابِع"}
              </button>
            )}
            <button
              onClick={toggleBlock}
              disabled={busy || (user.is_blocked && !user.blocked_by_me)}
              className="rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold disabled:opacity-50"
              style={{ color: "var(--mj-flag)", border: "1px solid var(--mj-line)" }}
              title={
                user.blocked_by_me
                  ? "إلغاء الحظر"
                  : user.is_blocked
                  ? "هذا العضو حظرك"
                  : "حظر — لن يرى أحدُكما منشورات الآخر"
              }
            >
              {user.blocked_by_me ? "إلغاء الحظر" : user.is_blocked ? "محظور" : "حظر"}
            </button>
          </div>
        )}
      </div>

      {user.is_blocked && (
        <p className="text-[12.5px] mt-2 px-1" style={{ color: "var(--mj-muted)" }}>
          {user.blocked_by_me
            ? "حظرتَ هذا العضو — لا يرى أحدُكما منشورات الآخر."
            : "لا يمكن عرض منشورات هذا العضو."}
        </p>
      )}

      {/* their posts */}
      <ul className="list-none p-0 m-0 mt-3">
        {results.map((post) => (
          <li key={post.id} className="mb-3">
            <PostCard
              post={post}
              showHarakat={showHarakat}
              onVote={onVote}
              onOpenComments={onOpenComments}
              commentsOpen={commentsOpenFor === post.id}
              isAdmin={isAdmin}
            />
            {commentsOpenFor === post.id && renderThread && renderThread(post)}
          </li>
        ))}
      </ul>
      {results.length === 0 && !user.is_blocked && (
        <p className="text-sm text-center py-6" style={{ color: "var(--mj-muted)" }}>
          لا منشوراتَ بعد.
        </p>
      )}
    </div>
  );
}
